import { defineComponent as _defineComponent } from 'vue';
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = { class: "flex flex-col leading-tight" };
const _hoisted_2 = { class: "font-semibold" };
import { computed, ref } from 'vue';
import { useOpeningHours } from '../composables/useOpeningHours';
export default /*@__PURE__*/ _defineComponent({
    ...{ name: 'DeliveryStatusBadge' },
    __name: 'DeliveryStatusBadge',
    props: {
        apiClient: {},
        label: { default: 'Lieferstatus' },
        openLabel: { default: 'Lieferzeit' },
        fallbackOpenText: { default: 'Wird berechnet...' },
        fallbackClosedText: { default: 'Momentan geschlossen' },
        closedPrefix: { default: 'Jetzt vorbestellen für' },
        closedSuffix: { default: 'Uhr' },
        appearance: { default: 'dark' },
        cacheKey: {},
        immediate: { type: Boolean, default: true },
        showLabel: { type: Boolean, default: true },
        showRetry: { type: Boolean, default: false },
        retryLabel: { default: 'Neu laden' },
        openUnit: { default: 'Minuten' },
        loadingHint: { default: 'Wird aktualisiert ...' },
        errorText: { default: 'Status aktuell nicht verfügbar' },
        errorSubline: { default: 'Bitte versuche es gleich erneut.' }
    },
    setup(__props) {
        const props = __props;
        const rootEl = ref(null);
        const { data, isLoading, error, refetch } = useOpeningHours({
            apiClient: props.apiClient,
            target: rootEl,
            cacheKey: props.cacheKey,
            immediate: props.immediate,
            once: true
        });
        const status = computed(() => data.value?.status);
        const timezone = computed(() => data.value?.timezone || status.value?.timezone || 'Europe/Berlin');
        const isOpen = computed(() => status.value?.isOpen ?? false);
        const hasResolvedData = computed(() => Boolean(data.value));
        const openingSoonText = computed(() => formatOpeningSoonText(status.value?.nextOpenTime));
        const deliverySection = computed(() => data.value?.delivery);
        const deliveryWindow = computed(() => {
            const delivery = deliverySection.value;
            if (!delivery)
                return '';
            const fromEstimated = delivery.estimatedDeliveryTime?.display?.trim();
            if (fromEstimated) {
                return fromEstimated;
            }
            const fromDelivery = delivery.deliveryTime?.display?.trim();
            if (fromDelivery) {
                return fromDelivery;
            }
            const min = formatRangeValue(delivery.deliveryTime?.min);
            const max = formatRangeValue(delivery.deliveryTime?.max);
            if (min && max) {
                if (min === max) {
                    return `${min} ${props.openUnit}`;
                }
                return `${min} - ${max} ${props.openUnit}`;
            }
            if (min || max) {
                return `${min || max} ${props.openUnit}`;
            }
            return delivery.message?.trim() || '';
        });
        const openWindowText = computed(() => deliveryWindow.value || props.fallbackOpenText);
        const primaryText = computed(() => {
            if (error.value) {
                return props.errorText;
            }
            if (!hasResolvedData.value) {
                return props.fallbackOpenText;
            }
            if (isOpen.value) {
                return `${props.openLabel}: ${openWindowText.value}`;
            }
            return openingSoonText.value || props.fallbackClosedText;
        });
        const secondaryText = computed(() => {
            if (error.value) {
                return props.errorSubline;
            }
            if (!hasResolvedData.value && isLoading.value) {
                return props.loadingHint;
            }
            if (!isOpen.value && status.value?.message) {
                return status.value.message;
            }
            return '';
        });
        const indicatorClass = computed(() => {
            if (error.value) {
                return props.appearance === 'light' ? 'bg-amber-500/80' : 'bg-amber-300';
            }
            if (!hasResolvedData.value && isLoading.value) {
                return props.appearance === 'light'
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-amber-300 animate-pulse';
            }
            if (isOpen.value) {
                return props.appearance === 'light' ? 'bg-emerald-500' : 'bg-emerald-400';
            }
            if (openingSoonText.value) {
                return props.appearance === 'light' ? 'bg-amber-500' : 'bg-amber-400';
            }
            return props.appearance === 'light' ? 'bg-rose-500' : 'bg-rose-400';
        });
        const labelClass = computed(() => (props.appearance === 'light' ? 'text-slate-500' : 'text-white/60'));
        const secondaryClass = computed(() => (props.appearance === 'light' ? 'text-slate-500' : 'text-white/70'));
        const showRetryButton = computed(() => props.showRetry && Boolean(error.value));
        function formatNextOpening(iso) {
            if (!iso) {
                return '';
            }
            try {
                const date = new Date(iso);
                if (Number.isNaN(date.getTime())) {
                    return '';
                }
                const time = new Intl.DateTimeFormat('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: timezone.value
                }).format(date);
                return `${props.closedPrefix} ${time}${props.closedSuffix ? ` ${props.closedSuffix}` : ''}`;
            }
            catch (formatError) {
                console.warn('[DeliveryStatusBadge] Failed to format next opening:', formatError);
                return '';
            }
        }
        function formatOpeningSoonText(nextOpenTime) {
            if (!nextOpenTime || isOpen.value) {
                return '';
            }
            const hhmmMatch = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(nextOpenTime.trim());
            if (hhmmMatch) {
                const [_, h, m] = hhmmMatch;
                const nowInTimezone = new Intl.DateTimeFormat('en-US', {
                    timeZone: timezone.value,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }).formatToParts(new Date());
                const nowHour = Number(nowInTimezone.find((part) => part.type === 'hour')?.value || '0');
                const nowMinute = Number(nowInTimezone.find((part) => part.type === 'minute')?.value || '0');
                const nowMinutes = (nowHour * 60) + nowMinute;
                const nextMinutes = (Number(h) * 60) + Number(m);
                const diffMinutes = nextMinutes - nowMinutes;
                if (diffMinutes >= 0 && diffMinutes <= 180) {
                    return `Öffnet bald ${h}:${m} Uhr`;
                }
                return '';
            }
            const formattedIso = formatNextOpening(nextOpenTime);
            if (formattedIso) {
                return formattedIso;
            }
            return '';
        }
        function formatRangeValue(value) {
            if (value === undefined || value === null) {
                return '';
            }
            return typeof value === 'number' ? String(value) : value;
        }
        const handleRetry = () => {
            refetch();
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "rootEl",
                ref: rootEl,
                role: "status",
                "aria-live": "polite",
                "aria-atomic": "true",
                class: "inline-flex items-center gap-3 px-4 py-2 text-sm"
            }, [
                _createElementVNode("span", {
                    class: _normalizeClass(["inline-flex h-2.5 w-2.5 rounded-full", indicatorClass.value]),
                    "aria-hidden": "true"
                }, null, 2 /* CLASS */),
                _createElementVNode("div", _hoisted_1, [
                    (props.showLabel)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            class: _normalizeClass(["text-xs uppercase", labelClass.value])
                        }, _toDisplayString(props.label), 3 /* TEXT, CLASS */))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("span", _hoisted_2, _toDisplayString(primaryText.value), 1 /* TEXT */)
                ])
            ], 512 /* NEED_PATCH */));
        };
    }
});
